import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import DefaultModal from './DefaultModal';
import useDefaultModal from '../hooks/useDefaultModal';

function ImagemModal({ show, imagem, onClose, onSave }) {
  const [formData, setFormData] = useState({
    idEntidade: '',
    orientacao: 'R',
    img: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('token');

  const vinculo = useMemo(() => {
    const stored = localStorage.getItem('vinculo');
    return stored ? JSON.parse(stored) : null;
  }, []);

  const entidade = vinculo?.entidade || {};
  const entidadeId = entidade.idEntidade || vinculo?.idEntidade;

  const { setDefaultModal, showDefaultModal } = useDefaultModal();

  useEffect(() => {
    if (!show) return;
    if (imagem) {
      setFormData({
        idEntidade: imagem.idEntidade || '',
        orientacao: imagem.orientacao || 'R',
        img: imagem.img || null,
      });
      if (imagem.img) {
        try {
          const bytes = atob(imagem.img);
          const array = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) array[i] = bytes.charCodeAt(i);
          const blob = new Blob([array], { type: 'image/png' });
          setImagePreview(URL.createObjectURL(blob));
        } catch {
          setImagePreview(null);
        }
      }
    } else {
      setFormData({
        idEntidade: entidadeId || '',
        orientacao: 'R',
        img: null,
      });
      setImagePreview(null);
    }
    setValidated(false);
    if (fileInputRef.current) { fileInputRef.current.value = ''; }
  }, [show]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const processImage = (file) => {
    if (!file.type.startsWith('image/')) {
      showDefaultModal('error', 'Por favor, selecione um arquivo de imagem válido.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showDefaultModal('error', 'A imagem deve ter no máximo 5MB.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      try {
        const dataUrl = reader.result;
        const parts = dataUrl.split(',');
        const base64String = parts.length > 1 ? parts[1] : dataUrl;
        setFormData(prev => ({ ...prev, img: base64String }));
        setImagePreview(dataUrl);
      } catch (error) {
        showDefaultModal('error', 'Erro ao processar a imagem.');
      }
    };
    reader.onerror = () => { showDefaultModal('error', 'Erro ao ler o arquivo de imagem.'); };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) processImage(file);
    if (e.target) e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) processImage(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleClick = () => { fileInputRef.current?.click(); };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setFormData(prev => ({ ...prev, img: null }));
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = e => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) { e.stopPropagation(); setValidated(true); return; }
    if (!formData.img) { showDefaultModal('error', 'Por favor, selecione uma imagem.'); return; }

    setLoading(true);
    const url = imagem ? `${API_BASE_URL.IMAGEM}/update` : `${API_BASE_URL.IMAGEM}/save`;
    const method = imagem ? 'put' : 'post';

    axios({ method, url, data: formData, headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        onSave(); onClose();
      })
      .catch(error => {
        showDefaultModal('error', error.response?.data?.message || 'Erro ao salvar imagem!');
      })
      .finally(() => { setLoading(false); });
  };

  if (!show) return null;

  return (
    <>
      <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header text-white" style={{
              background: 'linear-gradient(45deg, rgb(30, 27, 75) 0%, rgb(55, 48, 163) 100%)',
              padding: '0.5rem 1rem', borderTopLeftRadius: '0.5rem', borderTopRightRadius: '0.5rem', border: 'none',
            }}>
              <h6 className="modal-title mb-0">
                <i className="bi bi-image me-2"></i>
                {imagem ? 'Editar Imagem' : 'Nova Imagem'}
              </h6>
              <button type="button" className="btn-close btn-close-white" onClick={onClose} disabled={loading}></button>
            </div>
            <div className="modal-body">
              <form noValidate validated={validated.toString()} onSubmit={handleSubmit} id="imagemForm">
                <div className="row g-3">
                  <input type="hidden" name="idEntidade" value={formData.idEntidade} />
                  <div className="col-md-12">
                    <label htmlFor="orientacao" className="form-label">
                      Orientação <span className="text-danger">*</span>
                    </label>
                    <select className="form-select" id="orientacao" name="orientacao"
                      value={formData.orientacao} onChange={handleChange} required disabled={!!imagem}>
                      <option value="R">Retrato (Vertical)</option>
                      <option value="P">Paisagem (Horizontal)</option>
                    </select>
                    <div className="invalid-feedback">Campo obrigatório.</div>
                  </div>
                  <div className="col-12">
                    <label className="form-label">
                      Imagem <span className="text-danger">*</span>
                      {formData.img && (
                        <span className="badge bg-success ms-2">
                          <i className="bi bi-check-circle me-1"></i>
                          Carregada ({Math.round(formData.img.length / 1024)}KB)
                        </span>
                      )}
                    </label>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange}
                      style={{ display: 'none' }} />
                    <div className="border rounded p-4 text-center" style={{
                      borderStyle: 'dashed', borderColor: '#dee2e6', backgroundColor: '#f8f9fa',
                      cursor: 'pointer', transition: 'all 0.3s ease',
                    }}
                      onDrop={handleDrop} onDragOver={handleDragOver} onClick={handleClick} role="button" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }}>
                      {imagePreview ? (
                        <div className="position-relative d-inline-block">
                          <img src={imagePreview} alt="Preview da imagem"
                            style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '4px' }} />
                          <button type="button" className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
                            onClick={handleRemoveImage} title="Remover imagem">
                            <i className="bi bi-trash"></i>
                          </button>
                          <div className="mt-3">
                            <small className="text-muted d-block">Clique para selecionar outra imagem</small>
                          </div>
                        </div>
                      ) : (
                        <div style={{ padding: '20px' }}>
                          <i className="bi bi-cloud-upload"
                            style={{ fontSize: '3rem', color: '#6c757d', display: 'block', marginBottom: '10px' }}></i>
                          <p className="mb-2" style={{ fontSize: '1rem', fontWeight: '500' }}>Selecione uma imagem</p>
                          <p className="mb-3" style={{ color: '#6c757d' }}>Arraste e solte um arquivo aqui ou clique para procurar</p>
                          <small className="text-muted d-block">Formatos aceitos: PNG, JPG, JPEG (max. 5MB)</small>
                          <button type="button" className="btn btn-primary btn-sm mt-3"
                            onClick={(e) => { e.stopPropagation(); handleClick(); }}>
                            <i className="bi bi-folder2-open me-1"></i>Procurar Arquivo
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} disabled={loading}>Cancelar</button>
              <button type="submit" form="imagemForm" className="btn btn-success btn-sm" disabled={loading}>
                {loading ? (<><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Salvando...</>)
                  : (<><i className="bi bi-check-circle me-2"></i>Salvar</>)}
              </button>
            </div>
          </div>
        </div>
      </div>
      <DefaultModal {...setDefaultModal} />
    </>
  );
}

export default ImagemModal;
